import React, { useState } from "react";
import { createPortal } from "react-dom";
import { Link, useHistory } from "react-router-dom";
import AcademyAssignCourseModal from "src/academy/admin/AcademyAssignCourseModal/AcademyAssignCourseModal";
import AcademyCourseCategories from "src/academy/course/AcademyCourseCategories/AcademyCourseCategories";
import AcademyImage from "src/academy/course/AcademyImage/AcademyImage";
import AcademyCourseMetaDataRow from "src/academy/course/AcademyCourseMetaDataRow/AcademyCourseMetaDataRow";
import AcademyCourseProgress from "src/academy/course/AcademyCourseProgress/AcademyCourseProgress";
import { IAcademyCourse } from "src/academy/types";
import { Label } from "src/ui";
import "./AcademyCourseListItem.scss";
import StandardActionMenu from "src/ui/components/ActionMenu/StandardActionMenu";
import { useDispatch, useSelector } from "react-redux";
import api from "src/api";
import classNames from "classnames";
import { localize } from "src/l10n";
import { setConfirmPopup } from "src/popups/actions";
import { canAdministerAcademy } from "src/redux/selectors/academySelectors";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { Stack } from "@fluentui/react";
import { truncate } from "src/utils";
import dompurify from "dompurify";
import moment from "moment";

interface IProps {
    course: IAcademyCourse;
    big?: boolean;
    locked?: boolean;
    hideActionMenu?: boolean;
    isCoursePackage?: boolean;
    coursePackageId? :number;
    onPinToggle?: () => void;
    isPostShare?: boolean;
}

// NOTE: Also used in feed for shared courses
const AcademyCourseListItem = (props: IProps) => {
    const canAdminister = useSelector<Spintr.AppState, boolean>(canAdministerAcademy);

    const dispatch = useDispatch();
    const history = useHistory();
    const sanitizer = dompurify.sanitize;

    const [assignPanelOpen, setAssignPanelOpen] = useState(false);

    const canAssign =
        canAdminister &&
        !props.isCoursePackage &&
        !props.course.isGuide &&
        props.course.isPublished;

    const hasAdminDeadline =
        !!props.course.userProgress?.dueDate &&
        !props.course.userProgress?.isCompleted &&
        props.course.userProgress?.assignedById != null;

    const showRecurringReopen =
        props.course.userProgress?.recurringState === "reopen" &&
        !!props.course.userProgress?.validUntil &&
        !(props.course.userProgress?.dueDate && !props.course.userProgress?.isCompleted);

    const content = (
        <>
            <div className="AcademyCourseListItem-image">
                <AcademyImage imageUrl={props.course.imageUrl} />
                {!props.hideActionMenu && (
                    <StandardActionMenu
                        canShare={!props.isCoursePackage}
                        canEdit={canAdminister}
                        canPin={canAdminister && !props.isCoursePackage}
                        canDelete={canAdminister}
                        editorItems={
                            canAssign
                                ? [
                                      {
                                          text: localize("ASSIGN_COURSE"),
                                          onClick: () => setAssignPanelOpen(true),
                                          onRenderIcon: () => (
                                              <Visage2Icon icon="user-add" size="small" color="mid-grey" />
                                          ),
                                      },
                                  ]
                                : undefined
                        }
                        onEditClick={() => {
                            history.push(
                                `/learning/${props.isCoursePackage ? "packages/" : ""}${props.course.id}/edit`
                            );
                        }}
                        onDeleteClick={() => {
                            dispatch(setConfirmPopup({
                                isOpen: true,
                                title: localize("TaBort"),
                                message: localize("ArDuSakerPaAttDuVillGoraDetta"),
                                onConfirm: () => {
                                    api.post(
                                        `/api/v1/academy/${props.isCoursePackage ? "coursepackages" : "courses"}/${props.course.id}/toggle-deleted`
                                    ).then(() => {
                                        props.onPinToggle?.();
                                    });
                                },
                            }));
                        }}
                        onPinClick={async () => {
                            await api.post("/api/v1/objects/toggle-pin/" + props.course.id);
                            props.onPinToggle?.();
                        }}
                        isPinned={props.course.isPinned}
                        objectId={props.course.id}
                    />
                )}
            </div>
            <div className="AcademyCourseListItem-info">
                <div>
                    {props.isPostShare ? (
                        <Label size="h7" className="course-name" weight="medium" color="dark-grey">{props.course.name}</Label>
                    ) : (
                        <Label size="h3" className="course-name" weight="semi-bold" color="dark-grey">{props.course.name}</Label>
                    )}
                    {(props.course.category ||
                        (props.course.userProgress?.recurringState === "valid" &&
                            props.course.userProgress?.isCompleted) ||
                        props.course.userProgress?.recurringState === "expired" ||
                        showRecurringReopen ||
                        (props.course.userProgress?.isAssigned &&
                            !!props.course.userProgress?.dueDate &&
                            !props.course.userProgress?.isCompleted)) && (
                        <div className="AcademyCourseListItem-category-row">
                            {props.course.category && <AcademyCourseCategories categories={[props.course.category]} />}
                            {props.course.userProgress?.recurringState === "valid" &&
                                props.course.userProgress.isCompleted &&
                                props.course.userProgress.validUntil && (
                                <Label size="body-2" className="AcademyCourseListItem-recurring valid">
                                    {localize("VALID_UNTIL") + " " + moment(props.course.userProgress.validUntil).format("LL")}
                                </Label>
                            )}
                            {showRecurringReopen && (
                                    <Label size="body-2" className="AcademyCourseListItem-recurring due">
                                        {localize("DUE_PREFIX") + " " + moment(props.course.userProgress.dueDate || props.course.userProgress.validUntil).format("LL")}
                                    </Label>
                                )}
                            {props.course.userProgress?.recurringState === "expired" &&
                                props.course.userProgress.validUntil &&
                                !hasAdminDeadline && (
                                    <Label size="body-2" className="AcademyCourseListItem-recurring expired">
                                        {localize("EXPIRED_ON") + " " + moment(props.course.userProgress.validUntil).format("LL")}
                                    </Label>
                                )}
                            {props.course.userProgress?.isAssigned &&
                                props.course.userProgress.dueDate &&
                                !props.course.userProgress.isCompleted &&
                                (hasAdminDeadline || props.course.userProgress.recurringState !== "expired") && (
                                    props.course.userProgress.isOverdue ? (
                                        <Label size="body-2" className="AcademyCourseListItem-recurring expired">
                                            {localize("OVERDUE_PREFIX") + " " + moment(props.course.userProgress.dueDate).format("LL")}
                                        </Label>
                                    ) : (
                                        <Label size="body-2" className="AcademyCourseListItem-recurring due">
                                            {localize("DUE_PREFIX") + " " + moment(props.course.userProgress.dueDate).format("LL")}
                                        </Label>
                                    )
                                )}
                        </div>
                    )}
                    {props.course.description && (
                        props.isPostShare ? (
                            <Label className="shared-content-post-description" size="body-2" color="dark-grey">
                                {truncate(sanitizer(props.course.description, { ALLOWED_TAGS: [] }), 250)}
                            </Label>
                        ) : (
                            <Label
                                className="course-description"
                                size="body-1"
                                color="dark-grey"
                                dangerouslySetInnerHTML={{
                                    __html: truncate(sanitizer(props.course.description, { ALLOWED_TAGS: [] }), 250),
                                }}
                            />
                        )
                    )}
                </div>
                <div>
                    <AcademyCourseMetaDataRow
                        lessonCount={props.course.statistics.lessonCount}
                        userCount={props.course.statistics.usersTotal}
                        language={props.course.language}
                        languages={props.course.languages}
                        isCoursePackage={props.isCoursePackage}
                    />
                    {(props.course.userProgress.hasStarted || props.course.userProgress.completionPercent > 0) && !props.course.userProgress.isCompleted && (
                        <AcademyCourseProgress userProgress={props.course.userProgress} />
                    )}
                </div>
            </div>
            {props.locked && (
                <div className="locked">
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 6 }} horizontalAlign="center">
                        <Visage2Icon icon="lock" />
                        <Label size="body-2">{localize("COMPLETE_PREVIOUS_COURSE_TO_UNLOCK")}</Label>
                    </Stack>
                </div>
            )}
        </>
    );

    const className = classNames("AcademyCourseListItem", { big: props.big, locked: props.locked });

    const assignPanel = assignPanelOpen
        ? createPortal(
              <AcademyAssignCourseModal
                  courseId={props.course.id}
                  onClose={() => setAssignPanelOpen(false)}
              />,
              document.body
          )
        : null;

    return props.locked ? (
        <div className={className}>{content}</div>
    ) : (
        <>
            <Link
                to={
                    "/learning/" +
                    (props.isCoursePackage ? "packages/" : "") +
                    (props.coursePackageId ? "packages/" + props.coursePackageId + "/" : "") +
                    props.course.id
                }
                className={className}
            >
                {content}
            </Link>
            {assignPanel}
        </>
    );
};

export default AcademyCourseListItem;
